import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { T, useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import LinkButton from '@rewardopl/react-ui/link_button';

import { post } from '@rewardopl/utils/network';
import { stringify } from '@rewardopl/utils/url';

import { ButtonWrapper } from '../../../login/email/form.styles';
import { FormWrapper, Main, PageWrapper } from '../../../login/index.styles';

import Footer from '../../../../footer';
import Header from '../../../../header';
import Heading from '../../../../heading';
import LoadingText from '../../../../loading_text';

import { integrationsState } from '../../../../../store';

import { INTEGRATION_TYPES, INTEGRATIONS } from '../../../../../constants';

import type { Integration } from '@rewardopl/types';

function ContinueInApp() {
  const location = useLocation();

  return (
    <PageWrapper>
      <Header />
      <FormWrapper>
        <Main>
          <Heading align="center">Continue in app</Heading>
          <ButtonWrapper>
            <LinkButton
              as="a"
              className="primary"
              // @ts-expect-error - see https://github.com/styled-components/styled-components/issues/4112
              href={`rewardo-business://${location.pathname}${location.search}`}
            >
              <T>Continue in app</T>
            </LinkButton>
          </ButtonWrapper>
        </Main>
      </FormWrapper>
      <Footer />
    </PageWrapper>
  );
}

type AuthCallbackRedirectProps = {
  queryObj: Record<string, string>;
  returnPath: string;
};

let didSend = false;

function AuthCallbackRedirect({ queryObj, returnPath }: AuthCallbackRedirectProps) {
  const { provider } = useParams();
  const navigate = useNavigate();
  const [/* integrations */ , setIntegrations] = useAtom(integrationsState);
  const saveErrorString = useTranslation('Failed to connect integration');

  const action = `/api/auth/${provider}/callback${stringify(queryObj)}`;

  useEffect(() => {
    if (didSend) {
      return;
    }

    didSend = true;

    const validatedReturnPath =
      returnPath.startsWith('/') && !returnPath.startsWith('//') ? returnPath : '/';

    (async () => {
      try {
        const response = (await post(action)) as Integration;

        setIntegrations((oldItems) => oldItems.concat(response));

        navigate(validatedReturnPath);
      } catch {
        toast.error(saveErrorString);

        navigate('/');
      }

      didSend = false;
    })();
  }, [action, navigate, returnPath, saveErrorString, setIntegrations]);

  return <LoadingText />;
}

export default function AuthIntegrationCallback() {
  const { provider } = useParams();
  const [searchParams] = useSearchParams();

  const state = searchParams.get('state');

  const queryObj = (():
    | { token: string; cloudid: string }
    | { code: string; state: string }
    | Record<string, never>
    | null => {
    switch (provider) {
      case INTEGRATION_TYPES[INTEGRATIONS.DOTYKACKA]: {
        const token = searchParams.get('token');
        const cloudid = searchParams.get('cloudid');

        if (!token) {
          throw new Error('token is undefined');
        }

        if (!cloudid) {
          throw new Error('cloudid is undefined');
        }

        return { token, cloudid };
      }
      case INTEGRATION_TYPES[INTEGRATIONS.FOODSOFT]: {
        return {};
      }
      case INTEGRATION_TYPES[INTEGRATIONS.GOPOS]: {
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code) {
          throw new Error('code is undefined');
        }

        if (!state) {
          throw new Error('state is undefined');
        }

        return { code, state };
      }
      case INTEGRATION_TYPES[INTEGRATIONS.IZZYREST]: {
        return {};
      }
      case INTEGRATION_TYPES[INTEGRATIONS.SOFTMAR_MM]: {
        return {};
      }
      default:
        return null;
    }
  })();

  if (!queryObj) {
    return <Navigate to="/" />;
  }

  const decodedState = state
    ? (JSON.parse(decodeURIComponent(state)) as {
        mobile?: boolean;
        returnPath?: string;
      })
    : null;

  const isMobileLogin = decodedState?.mobile;
  const returnPath = decodedState?.returnPath || '/';

  if (isMobileLogin) {
    return <ContinueInApp />;
  }

  return <AuthCallbackRedirect queryObj={queryObj} returnPath={returnPath} />;
}
